import { useCallback, useMemo } from 'react';
import type GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { useEriCounties } from 'hooks/map/useEriCounties';
import { usePowerOutageCounties } from 'hooks/map/usePowerOutageCounties';
import { useUscgMapContext } from './UscgMapContext';
import type { ArcgisMapView } from './utils/arcgisView';
import {
  ERI_LAYER_ID,
  POWER_OUTAGE_LAYER_ID,
  formatEriStepLabel,
} from './utils/outageLayerStyles';

export const POWER_OUTAGE_LAYER_TITLE = 'Power Outages';
export const ERI_LAYER_TITLE = 'Outage Prediction (ERI)';

/**
 * Zoom to the full extent of one of the county choropleth layers once ArcGIS
 * has loaded it.
 */
async function zoomToChoroplethLayer(
  mapView: ArcgisMapView | null,
  layerId: string
) {
  if (!mapView?.map) return;
  const layer = mapView.map.findLayerById(layerId) as GeoJSONLayer | undefined;
  if (!layer) return;
  try {
    await layer.when();
    if (layer.fullExtent) {
      await mapView.goTo(layer.fullExtent, { duration: 500 });
    }
  } catch (err) {
    console.warn(`Failed to zoom to layer ${layerId}`, err);
  }
}

/**
 * State + actions shared by the Layers tab row and the SitStat row for the
 * "Power Outages" layer. The react-query hook is keyed identically to the one
 * the map sync uses, so both read one cache entry.
 */
export function usePowerOutageLayerControls() {
  const { mapView, isPowerOutageVisible, togglePowerOutageVisibility } =
    useUscgMapContext();
  const { data, isLoading, isError } =
    usePowerOutageCounties(isPowerOutageVisible);

  const featureCount = data?.featureCollection.features.length ?? 0;
  const hasData = Boolean(data?.asOf) && featureCount > 0;
  const summary = useMemo(() => {
    if (!data) return null;
    let out = 0;
    let tracked = 0;
    for (const f of data.featureCollection.features) {
      out += f.properties.customersOut;
      tracked += f.properties.customersTracked;
    }
    return { out, tracked };
  }, [data]);

  const zoomToLayer = useCallback(() => {
    if (!isPowerOutageVisible) togglePowerOutageVisibility();
    void zoomToChoroplethLayer(mapView, POWER_OUTAGE_LAYER_ID);
  }, [mapView, isPowerOutageVisible, togglePowerOutageVisibility]);

  return {
    isVisible: isPowerOutageVisible,
    toggleVisibility: togglePowerOutageVisibility,
    isLoading: isPowerOutageVisible && isLoading,
    isError,
    hasData,
    /**
     * Only meaningful once the layer has been shown at least once (data is
     * fetched lazily).
     */
    fetched: data !== undefined,
    asOfLabel: data?.asOf ? formatDateTime(data.asOf) : null,
    featureCount,
    summary,
    zoomToLayer,
    zoomDisabled: !hasData,
  };
}

/**
 * State + actions shared by the Layers tab row and the SitStat row for the
 * "Outage Prediction (ERI)" layer.
 */
export function useEriLayerControls() {
  const { mapView, isEriVisible, toggleEriVisibility, eriStep, setEriStep } =
    useUscgMapContext();
  const { data, isLoading, isError } = useEriCounties(isEriVisible, eriStep);

  const featureCount = data?.featureCollection.features.length ?? 0;
  const hasData = Boolean(data?.modelRuntime) && featureCount > 0;
  const steps = data?.steps;
  const selectedStep = data?.selectedStep ?? eriStep;

  const stepOptions = useMemo(
    () =>
      (steps ?? []).map((step) => ({
        value: step,
        label: formatEriStepLabel(step),
      })),
    [steps]
  );

  const zoomToLayer = useCallback(() => {
    if (!isEriVisible) toggleEriVisibility();
    void zoomToChoroplethLayer(mapView, ERI_LAYER_ID);
  }, [mapView, isEriVisible, toggleEriVisibility]);

  return {
    isVisible: isEriVisible,
    toggleVisibility: toggleEriVisibility,
    isLoading: isEriVisible && isLoading,
    isError,
    hasData,
    fetched: data !== undefined,
    modelRuntimeLabel: data?.modelRuntime
      ? formatDateTime(data.modelRuntime)
      : null,
    staleLabel:
      data?.stale && data.modelAgeHours != null
        ? `Model run is ${Math.round(data.modelAgeHours)} h old`
        : null,
    selectedStep,
    selectedStepLabel: selectedStep ? formatEriStepLabel(selectedStep) : null,
    stepOptions,
    setStep: setEriStep,
    featureCount,
    zoomToLayer,
    zoomDisabled: !hasData,
  };
}
